import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { useState } from 'react';
import styled from 'styled-components';

import FormEditCustomLocation, {
  FormEditCustomLocationData,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/FormEditCustomLocation';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { ModuleLocation } from 'dashboard/modules/Location';
import { CustomLocation } from 'dashboard/modules/Location/model';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const CustomLocationHeader = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: 2fr 1fr;
  align-items: center;
`;

const CustomLocationsContainer = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[5]};
`;

const StyledTableTD = styled(TableTD)`
  width: 100%;
`;

export interface LocationPanelProps {
  module: ModuleLocation;
  updateModule: (module: ModuleLocation) => void;
}

const LocationPanel = ({ module, updateModule }: LocationPanelProps) => {
  const [
    editCustomLocationModal,
    { enable: showEditCustomLocationModal, disable: hideEditCustomLocationModal },
  ] = useToggle(false);
  const [currentCustomLocationIndex, setCurrentCustomLocationIndex] = useState<number>(0);
  const caseCustomLocations = module['custom-locations'] ?? [];

  const deleteCustomLocation = (index: number) =>
    updateModule(
      produce(module, (draft) => {
        draft['custom-locations'] ??= [];
        draft['custom-locations'].splice(index, 1);
        return draft;
      }),
    );
  const updateCustomLocation = (index: number, location: CustomLocation) =>
    updateModule(
      produce(module, (draft) => {
        draft['custom-locations'] ??= [];
        draft['custom-locations'].splice(index, 1, location);
        return draft;
      }),
    );
  const moreItems = (index: number) => {
    return [
      {
        id: 'location-panel-templates__edit_button',
        label: 'Edit',
        variant: 'basic' as const,
        onClick: () => {
          setCurrentCustomLocationIndex(index);
          showEditCustomLocationModal();
        },
      },
      {
        id: 'location-panel-templates__delete_button',
        label: 'Delete',
        variant: 'danger' as const,
        onClick: () => deleteCustomLocation(index),
      },
    ];
  };

  const handleUpdateCustomLocation = (customLocationData: FormEditCustomLocationData) => {
    updateCustomLocation(currentCustomLocationIndex, customLocationData);
    hideEditCustomLocationModal();
  };

  const handleAddCustomLocation = () => {
    setCurrentCustomLocationIndex(module['custom-locations']?.length ?? 0);
    showEditCustomLocationModal();
  };

  return (
    <>
      <CustomLocationsContainer>
        <CustomLocationHeader>
          <Heading size='sm'>Location Overlays</Heading>
          <Button
            id='location-panel__add-custom-location'
            variant='inverse'
            prefixIcon={<PlusIcon />}
            width='auto'
            onClick={handleAddCustomLocation}
          >
            Add
          </Button>
        </CustomLocationHeader>
        <Spacer height={1} />
        <Paragraph color='secondary'>
          Add fixed location overlays to your cases to aid with manual review.
        </Paragraph>
        <Spacer height={1} />
        <TableLayout
          showHeader={caseCustomLocations.length > 0}
          isEmpty={caseCustomLocations.length === 0}
          nullState={<></>}
          headerTR={
            <>
              <TableTH>Location Overlays</TableTH>
              <TableTH></TableTH>
            </>
          }
        >
          {caseCustomLocations.map(
            ({ name, latitude, longitude, 'radius-meters': radiusMeters }, index) => {
              const locationLabel = `${stringifyLatitude(latitude)}, ${stringifyLongitude(
                longitude,
              )} (${formatNumber(radiusMeters)}m)`;

              return (
                <TableTR key={index}>
                  <StyledTableTD>
                    <Paragraph color='primary'>{name}</Paragraph>
                    <Paragraph color='secondary'>{locationLabel}</Paragraph>
                  </StyledTableTD>
                  <StyledTableTD>
                    <DropdownMenu
                      trigger={
                        <Button
                          id='location-panel-row__dropdown'
                          variant='inverse'
                          width='icon'
                          aria-label='More'
                        >
                          <MoreIcon />
                        </Button>
                      }
                    >
                      {moreItems(index).map((item) => (
                        <DropdownMenu.Item
                          id={item.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            item.onClick();
                          }}
                          key={item.id}
                          variant={item.variant}
                        >
                          {item.label}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu>
                  </StyledTableTD>
                </TableTR>
              );
            },
          )}
        </TableLayout>
      </CustomLocationsContainer>
      <ModalWrapper isOpen={editCustomLocationModal} handleCloseModal={hideEditCustomLocationModal}>
        <ModalContainer>
          <ModalHeader onClose={hideEditCustomLocationModal}>
            Edit{' '}
            {module['custom-locations']?.[currentCustomLocationIndex]?.name ??
              'new custom location'}
          </ModalHeader>
          <ModalBody>
            <FormEditCustomLocation
              defaultValues={
                module['custom-locations']?.[currentCustomLocationIndex] ?? {
                  name: 'New custom location',
                  latitude: 0,
                  longitude: 0,
                  'radius-meters': 1000,
                }
              }
              handleClose={hideEditCustomLocationModal}
              handleUpdateCustomLocation={handleUpdateCustomLocation}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default LocationPanel;
